import { api } from "../../../lib/axios";
import { apiSlice, STORAGE_KEY } from "../../../features/api/apiSlice";
import { ACTIVE_ROLE_KEY } from "../../../context/AuthContexts";

type LoginPayload = { email: string; password: string };
type RegisterPayload = {
  email: string;
  phone: string;
  user_category_id: number;
};

type ForgotPasswordPayload = { email: string };

type ResetPasswordPayload = {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
};

type ChangePasswordPayload = {
  current_password: string;
  password: string;
  password_confirmation: string;
};

export const login = (data: { email: string; password: string }) =>
  api.post("/auth/login", data);

export const logout = () => api.post("auth/logout");

// ✅ helper to notify AuthContext in same tab
const notifyAuthUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth:updated"));
  }
};

export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<any, LoginPayload>({
      query: (creds) => ({
        url: "/auth/login",
        method: "POST",
        body: creds,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data?.token || !data?.user) {
            localStorage.removeItem("token");
            localStorage.removeItem(STORAGE_KEY);
            notifyAuthUpdated();
            return;
          }

          const authData = {
            ...data.user,
            token: data.token,
            onboardingComplete: true,
            has_application: !!data.last_application,
            application_status: data.last_application?.status ?? null,
          };

          localStorage.setItem("token", data.token);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

          // Set active role to the first role returned
          const roles = data.user?.roles || [];
          const firstRole = roles[0];
          if (firstRole) {
            const name = typeof firstRole === "string" ? firstRole : firstRole?.name;
            if (name) localStorage.setItem(ACTIVE_ROLE_KEY, name);
          }

          notifyAuthUpdated();
        } catch {
          localStorage.removeItem("token");
          localStorage.removeItem(STORAGE_KEY);
          notifyAuthUpdated();
        }
      },
    }),

    register: builder.mutation<any, RegisterPayload>({
      query: (payload) => ({
        url: "auth/control",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data?.token || !data?.user) {
            localStorage.removeItem("token");
            localStorage.removeItem(STORAGE_KEY);
            notifyAuthUpdated();
            return;
          }

          const authData = {
            ...data.user,
            token: data.token,
            onboardingComplete: true,
          };

          localStorage.setItem("token", data.token);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

          notifyAuthUpdated();
        } catch {
          localStorage.removeItem("token");
          localStorage.removeItem(STORAGE_KEY);
          notifyAuthUpdated();
        }
      },
    }),

    // FORGOT PASSWORD (public)
    forgotPassword: builder.mutation<any, ForgotPasswordPayload>({
      query: (payload) => ({
        url: "auth/forgot-password",
        method: "POST",
        body: payload,
      }),
    }),

    // RESET PASSWORD (public)
    resetPassword: builder.mutation<any, ResetPasswordPayload>({
      query: (payload) => ({
        url: "auth/reset-password",
        method: "POST",
        body: payload,
      }),
    }),

    // CHANGE PASSWORD (auth)
    changePassword: builder.mutation<any, ChangePasswordPayload>({
      query: (payload) => ({
        url: "auth/change-password",
        method: "POST",
        body: payload,
      }),
      // WAS NESTED INSIDE query() and therefore inert — RTK Query reads
      // invalidatesTags off the ENDPOINT, not off the request args, so nothing
      // was ever invalidated and /me kept serving a cached changed_password of
      // false. The onboarding guard reads that flag, so the member was sent
      // straight back to /change-password after changing it.
      invalidatesTags: ["Auth"],

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // A rejected change must not mark the flag. Nothing to undo: the
          // patch below only runs once the server has confirmed.
          return;
        }

        // The guard gates on changed_password, so set it the moment the server
        // confirms rather than waiting on the /me round-trip. Without this the
        // release depends on a refetch landing before the page navigates, which
        // is a race — and on a slow link it is a race the redirect wins.
        dispatch(
          apiSlice.util.updateQueryData("me" as never, undefined as never, (draft: any) => {
            if (draft?.user) draft.user.changed_password = true;
          })
        );

        // The guard also reads localStorage, and falls back to it whenever /me
        // has not resolved. Leaving it stale reintroduces the same bounce on the
        // next page load.
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const stored = JSON.parse(raw);
            stored.changed_password = true;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
          }
        } catch {
          // A corrupt stored blob is not worth failing a successful change over.
        }

        notifyAuthUpdated();
      },
    }),

    logout: builder.mutation<any, void>({
      query: () => ({
        url: "auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // backend can fail but we still logout locally
        } finally {
          localStorage.removeItem("token");
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ACTIVE_ROLE_KEY);
          notifyAuthUpdated();

          dispatch(apiSlice.util.resetApiState());
          if (typeof window !== "undefined") window.location.href = "/login";
        }
      },
    }),

    resendVerification: builder.mutation<any, void>({
      query: () => ({
        url: "auth/email/resend",
        method: "POST",
      }),
    }),

    // me: builder.query<any, void>({
    //   query: () => ({
    //     url: "auth/me",
    //     method: "GET",
    //   }),
    // }),

    me: builder.query<any, void>({
      query: () => ({
        url: "auth/me",
        method: "GET",
      }),
      providesTags: ["Auth"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) {
            // Keep localStorage in sync with the fresh DB data
            const currentRaw = localStorage.getItem(STORAGE_KEY);
            const currentData = currentRaw ? JSON.parse(currentRaw) : {};

            // Preserve richer role objects (with permissions) from login if
            // the /me endpoint returns only plain string roles
            const incomingRoles = data.user?.roles || [];
            const storedRoles = currentData?.roles || [];
            const incomingHasObjects = incomingRoles.some(
              (r: any) => typeof r === "object"
            );
            const storedHasObjects = storedRoles.some(
              (r: any) => typeof r === "object"
            );
            const roles =
              !incomingHasObjects && storedHasObjects
                ? storedRoles
                : incomingRoles;

            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({
                ...currentData,
                ...data.user,
                roles,
              })
            );

            // Notify AuthContext so its React state reflects the latest me() data
            notifyAuthUpdated();
          }
        } catch {
          // If unauthorized, the interceptor or Guard will handle redirect
        }
      },
    }),

    getUserCategories: builder.query<any, void>({
      query: () => "/user-categories/active",
    }),

    // completeProfile: builder.mutation({
    //   query: (data) => ({
    //     url: "/profile/update",
    //     method: "POST",
    //     body: data,
    //     invalidatesTags: ["Auth"],
    //   }),
    // }),

    completeProfile: builder.mutation({
      query: (data) => ({
        url: "/profile/update",
        method: "POST",
        body: data, // must be FormData
      }),
      invalidatesTags: ["Auth"],
    }),

    uploadProfilePhoto: builder.mutation<any, FormData>({
      query: (data) => ({
        url: "/profile/update",
        method: "POST",
        body: data,
        formData: true,
      }),
      invalidatesTags: ["Auth"],
    }),

    getProfileMeta: builder.query<any, void>({
      query: () => "/profile/meta",
    }),
    getProfileMetaMe: builder.query<any, void>({
      query: () => "/profile/metame",
    }),
    getProfile: builder.query<any, void>({
      query: () => "/profile",
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useResendVerificationMutation,
  useMeQuery,
  useLazyMeQuery,
  useGetUserCategoriesQuery,
  useCompleteProfileMutation,
  useUploadProfilePhotoMutation,
  useGetProfileMetaQuery,
  useGetProfileQuery,
  useGetProfileMetaMeQuery,
} = authApi;
